import { FC, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { Outlet } from 'react-router-dom';

import { ReviewSolicitationsModal } from '@bonusly/common-onboarding';
import { Box, Grid, rem, useBreakpointValue } from '@bonusly/design-system';

import { App as AdminNav } from '../../modules/admin-navbar/components/app/app';
import { useCelebrationEventTracking } from '../../modules/celebrations/hooks';
import { useBizyAdminLayout } from '../bizy-side-panel/hooks/use-bizy-admin-layout';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { AdminRoute } from '../routes/AdminRoute';
import { MobileUnavailable } from '../routes/statuses/MobileUnavailable';
import { useSpoofing } from './hooks/useSpoofing';

const { GlobalModals } = lazily(() => import('../../app/global-modals'));
const { BizySidePanel } = lazily(() => import('../bizy-side-panel'));

const ADMIN_NAV_WIDTH = 200;

export const AdminAppLayout: FC = () => {
	// Enable celebration event tracking for Intercom integration
	useCelebrationEventTracking();

	const { data } = useSpoofing();
	const isMobile = useBreakpointValue({ base: true, xs: false });
	const bizy = useBizyAdminLayout({ isMobile: isMobile ?? false });

	if (isMobile) {
		return <MobileUnavailable />;
	}

	const isSpoofing = data?.is_spoofing && !data?.is_demoing;
	const spoofingBannerHeight = rem(60);
	const panelLeft = bizy.isPanelOpen ? rem(ADMIN_NAV_WIDTH) : rem(ADMIN_NAV_WIDTH - bizy.panelWidth);

	return (
		<>
			<ReviewSolicitationsModal />
			<Suspense>
				<GlobalModals />
			</Suspense>

			<Box pos="relative">
				{isSpoofing && <SpoofingWarning />}
				<Grid
					bg="bg.subtle"
					h="100vh"
					minW={rem(370)}
					gridTemplateColumns={{
						base: '1fr',
						xs: `${rem(ADMIN_NAV_WIDTH)} 1fr`,
					}}
				>
					<Box position="relative" zIndex={11}>
						<AdminNav h="100vh" />
					</Box>
					<Box
						as="main"
						overflowY="auto"
						h="100vh"
						pl={bizy.isEnabled && bizy.isPanelOpen ? rem(bizy.panelWidth) : 0}
						transition={bizy.isResizing ? 'none' : 'padding-left 0.35s cubic-bezier(.4,0,.2,1)'}
					>
						<AdminRoute>
							<Suspense>
								<Outlet />
							</Suspense>
						</AdminRoute>
					</Box>
				</Grid>

				{/* Bizy side panel — slides in between nav and main content */}
				{bizy.isEnabled && (
					<Suspense fallback={null}>
						<Box
							position="fixed"
							left={panelLeft}
							top={isSpoofing ? spoofingBannerHeight : '0'}
							height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
							width={rem(bizy.panelWidth)}
							bg="bg.surface"
							borderRight={bizy.isPanelOpen ? '1px solid' : 'none'}
							borderColor="border.default"
							zIndex="docked"
							transition={bizy.isResizing ? 'none' : 'left 0.35s cubic-bezier(.4,0,.2,1), width 0.35s cubic-bezier(.4,0,.2,1)'}
						>
							<BizySidePanel panelLeftEdge={ADMIN_NAV_WIDTH} />
						</Box>
					</Suspense>
				)}
			</Box>
		</>
	);
};
